import {
  CustomerAction,
  AnnotationAction,
  HandId,
  HandSide,
  Journey,
  JourneyEvent,
} from "@/entities/annotation";
import {
  JourneyEventRequest,
  JourneyEventResponse,
  JourneyProximity,
  JourneyProximityRequest,
  JourneyProximityResponse,
  JourneyTransaction,
  JourneyTransactionRequest,
  JourneyTransactionResponse,
  Order,
  ProximityTimeRange,
  ProximityTimeRangeRequest,
  ProximityTimeRangeResponse,
  TrackPersistence,
  TrackPersistenceRequest,
  TrackPersistenceResponse,
} from "@/shared/api/lod-api";
import dayjs from "dayjs";
import {
  OrderFilters,
  PinBasedPrediction,
  PinBasedPredictionJson,
  PinBasedPredictionRecord,
  SortDirection,
  SourceOption,
  ZoneBasedPrediction,
  ZoneBasedPredictionJson,
  ZoneBasedPredictionRecord,
} from "./types";
import { isValid, parse } from "date-fns";
import { SORT_DIRECTIONS, TIMELINE_ITEM_COLORS } from "./consts";
import { DateRange } from "react-day-picker";
import { Gondola } from "@/shared/api";

import {
  ANNOTATION_TYPES,
  CUSTOMER_ACTIONS,
  ANNOTATION_ACTIONS,
  ANNOTATION_RECORD_STATUSES,
  HAND_IDS,
  PREDICTION_TYPES,
} from "@/entities/annotation/model/consts";
import { DataSet } from "vis-data";

type TimestampValue = Date | string | number;

interface HasTimestamp {
  timestamp: TimestampValue;
}

type EventSource = {
  id: string;
};

type EventCollections = {
  events: JourneyEvent[];
  journeyTransactions: JourneyTransaction[];
  trackPersistences: TrackPersistence[];
  journeyProximities: JourneyProximity[];
  proximityTimeRanges: ProximityTimeRange[];
  pinBasedPredictions: PinBasedPrediction[];
  zoneBasedPredictions: ZoneBasedPrediction[];
};

interface GroupedAnnotations<R> {
  created: Record<string, R[]>;
  deleted: Record<string, Array<R & { id: string | number }>>;
}

export type OrderWithJourneys = Order & {
  journeys: Array<
    JourneyEventResponse & {
      transaction_annotations?: JourneyTransactionResponse[];
      persistence_annotations?: TrackPersistenceResponse[];
      proximity_annotations?: Array<
        JourneyProximityResponse & {
          range_annotations?: ProximityTimeRangeResponse[];
        }
      >;
    }
  >;
};

export type OrderAnnotations = {
  order: OrderWithJourneys;
  journeyList: Journey[];
  events: JourneyEvent[];
  journeyTransactions: JourneyTransaction[];
  trackPersistences: TrackPersistence[];
  journeyProximities: JourneyProximity[];
  proximityTimeRanges: ProximityTimeRange[];
  pinBasedPredictions: PinBasedPrediction[];
  zoneBasedPredictions: ZoneBasedPrediction[];
  journeyAnalysis: {
    journeyId: string;
    hasTransactions: boolean;
    hasTrackPersistences: boolean;
    hasJourneyProximities: boolean;
    hasLeftHandProximityTimeRanges: boolean;
    hasRightHandProximityTimeRanges: boolean;
  }[];
  firstJourneyId: string | null;
};

export const extractAnnotationsFromOrder = (
  order: OrderWithJourneys
): OrderAnnotations => {
  const events: JourneyEvent[] = [];
  const journeyTransactions: JourneyTransaction[] = [];
  const trackPersistences: TrackPersistence[] = [];
  const journeyProximities: JourneyProximity[] = [];
  const proximityTimeRanges: ProximityTimeRange[] = [];
  const pinBasedPredictions: PinBasedPrediction[] = [];
  const zoneBasedPredictions: ZoneBasedPrediction[] = [];

  const journeyList = order.journeys.map((journey) => ({
    id: journey.id || "",
    status: ANNOTATION_RECORD_STATUSES.UNCHANGED,
    note: journey.note,
  }));

  const firstJourneyId = journeyList.length > 0 ? journeyList[0].id : null;

  order.journeys.forEach((journeyEvent) => {
    const enterEvent = extractEnterEventFromJourney({
      journeyEvent,
      orderId: order.id,
    });
    events.push(enterEvent);

    const exitEvent = extractExitEventFromJourney({
      journeyEvent,
      orderId: order.id,
    });
    events.push(exitEvent);

    if (
      journeyEvent.transaction_annotations &&
      journeyEvent.transaction_annotations.length > 0
    ) {
      journeyEvent.transaction_annotations.forEach((transactionRecord) => {
        const transaction = transformRecordToTransaction({
          record: transactionRecord,
          orderId: order.id,
          journeyId: journeyEvent.id,
        });
        journeyTransactions.push(transaction);
      });
    }

    if (
      journeyEvent.persistence_annotations &&
      journeyEvent.persistence_annotations.length > 0
    ) {
      journeyEvent.persistence_annotations.forEach((persistenceRecord) => {
        const persistence = transformRecordToTrackPersistence({
          record: persistenceRecord,
          orderId: order.id,
          journeyId: journeyEvent.id,
        });
        trackPersistences.push(persistence);
      });
    }

    if (
      journeyEvent.proximity_annotations &&
      journeyEvent.proximity_annotations.length > 0
    ) {
      journeyEvent.proximity_annotations.forEach((proximityRecord) => {
        const proximity = transformRecordToJourneyProximity({
          record: proximityRecord,
          orderId: order.id,
          journeyId: journeyEvent.id,
        });
        journeyProximities.push(proximity);

        if (
          proximityRecord.range_annotations &&
          proximityRecord.range_annotations.length > 0
        ) {
          proximityRecord.range_annotations.forEach((rangeRecord) => {
            const timeRange = transformRecordToProximityTimeRange({
              record: rangeRecord,
              proximity,
              orderId: order.id,
              journeyId: journeyEvent.id,
            });
            proximityTimeRanges.push(timeRange);
          });
        }
      });
    }
  });

  const journeyAnalysis = order.journeys.map((journey) => {
    const hasTransactions = journeyTransactions.some(
      (transaction) => transaction.journeyId === journey.id
    );
    const hasTrackPersistences = trackPersistences.some(
      (trackPersistence) => trackPersistence.journeyId === journey.id
    );
    const hasJourneyProximities = journeyProximities.some(
      (journeyProximity) => journeyProximity.journeyId === journey.id
    );
    const hasLeftHandProximityTimeRanges = proximityTimeRanges.some(
      (timeRange) =>
        timeRange.journeyId === journey.id && timeRange.handId === HAND_IDS.LEFT
    );
    const hasRightHandProximityTimeRanges = proximityTimeRanges.some(
      (timeRange) =>
        timeRange.journeyId === journey.id &&
        timeRange.handId === HAND_IDS.RIGHT
    );

    return {
      journeyId: journey.id,
      hasTransactions,
      hasTrackPersistences,
      hasJourneyProximities,
      hasLeftHandProximityTimeRanges,
      hasRightHandProximityTimeRanges,
    };
  });

  return {
    order,
    journeyList,
    events,
    journeyTransactions,
    trackPersistences,
    journeyProximities,
    proximityTimeRanges,
    pinBasedPredictions,
    zoneBasedPredictions,
    journeyAnalysis,
    firstJourneyId,
  };
};

export const extractEnterEventFromJourney = ({
  journeyEvent,
  orderId,
}: {
  journeyEvent: JourneyEventResponse;
  orderId: string;
}): JourneyEvent => ({
  id: crypto.randomUUID(),
  pinCoordinates: {
    relativeX: journeyEvent.enter_pin_xy[0],
    relativeY: journeyEvent.enter_pin_xy[1],
  },
  type: ANNOTATION_TYPES.JOURNEY,
  eventType: "enter",
  journeyId: journeyEvent.id,
  zone: {
    id: journeyEvent.enter_zone.zone_id,
    description: journeyEvent.enter_zone.zone_name,
    projection: journeyEvent.enter_zone.zone_2d_polygon,
    polygon3d: journeyEvent.enter_zone.zone_3d_polygon,
  },
  notes: journeyEvent.enter_note,
  orderId: orderId,
  cameraId: journeyEvent.enter_camera_id,
  timestamp: new Date(journeyEvent.enter_time), // Dash player date
});

export const extractExitEventFromJourney = ({
  journeyEvent,
  orderId,
}: {
  journeyEvent: JourneyEventResponse;
  orderId: string;
}): JourneyEvent => ({
  id: crypto.randomUUID(),
  pinCoordinates: {
    relativeX: journeyEvent.exit_pin_xy[0],
    relativeY: journeyEvent.exit_pin_xy[1],
  },
  type: ANNOTATION_TYPES.JOURNEY,
  eventType: "exit",
  journeyId: journeyEvent.id,
  zone: {
    id: journeyEvent.exit_zone.zone_id,
    description: journeyEvent.exit_zone.zone_name,
    projection: journeyEvent.exit_zone.zone_2d_polygon,
    polygon3d: journeyEvent.exit_zone.zone_3d_polygon,
  },
  notes: journeyEvent.exit_note,
  orderId: orderId,
  cameraId: journeyEvent.exit_camera_id,
  timestamp: new Date(journeyEvent.exit_time), // Dash player date
});

export const createJourneyPayload = ({
  enterEvent,
  exitEvent,
  videoElement,
  note,
}: {
  enterEvent: JourneyEvent;
  exitEvent: JourneyEvent;
  videoElement: HTMLVideoElement;
  note: string;
}): JourneyEventRequest => ({
  enter_camera_id: enterEvent.cameraId,
  enter_pin_xy: [
    enterEvent.pinCoordinates.relativeX,
    enterEvent.pinCoordinates.relativeY,
  ],
  enter_time: enterEvent.timestamp.toISOString(),
  enter_zone: {
    zone_2d_polygon: enterEvent.zone.projection,
    zone_3d_polygon: enterEvent.zone.polygon3d,
    zone_id: enterEvent.zone.id,
    zone_name: enterEvent.zone.description,
  },
  exit_camera_id: exitEvent.cameraId,
  exit_pin_xy: [
    exitEvent.pinCoordinates.relativeX,
    exitEvent.pinCoordinates.relativeY,
  ],
  exit_time: exitEvent.timestamp.toISOString(),
  exit_zone: {
    zone_2d_polygon: exitEvent.zone.projection,
    zone_3d_polygon: exitEvent.zone.polygon3d,
    zone_id: exitEvent.zone.id,
    zone_name: exitEvent.zone.description,
  },
  note,
  enter_note: enterEvent.notes || "",
  exit_note: exitEvent.notes || "",
  enter_camera_image_width: videoElement.videoWidth,
  enter_camera_image_height: videoElement.videoHeight,
  exit_camera_image_width: videoElement.videoWidth,
  exit_camera_image_height: videoElement.videoHeight,
});

const toCustomerAction = (
  annotationAction: AnnotationAction
): CustomerAction => {
  return annotationAction === ANNOTATION_ACTIONS.ADD
    ? CUSTOMER_ACTIONS.TAKE
    : CUSTOMER_ACTIONS.PUT;
};

const toAnnotationAction = (
  customerAction: CustomerAction
): AnnotationAction => {
  return customerAction === CUSTOMER_ACTIONS.TAKE
    ? ANNOTATION_ACTIONS.ADD
    : ANNOTATION_ACTIONS.REMOVE;
};

export const groupAnnotationRecordsByKeyAndStatus = <
  T extends { id: string | number; [key: string]: any },
  R,
>(
  records: T[],
  transform: (record: T) => R,
  groupByKey = "journeyId",
  statusKey = "status"
): GroupedAnnotations<R> => {
  return records.reduce(
    (acc, record) => {
      const transformedRecord = transform(record);
      const groupValue = String(record[groupByKey]);
      const status = record[statusKey];

      if (status === ANNOTATION_RECORD_STATUSES.NEW) {
        if (!acc.created[groupValue]) {
          acc.created[groupValue] = [];
        }
        acc.created[groupValue].push(transformedRecord);
      } else if (status === ANNOTATION_RECORD_STATUSES.DELETED) {
        if (!acc.deleted[groupValue]) {
          acc.deleted[groupValue] = [];
        }
        acc.deleted[groupValue].push({ ...transformedRecord, id: record.id });
      }

      return acc;
    },
    { created: {}, deleted: {} } as GroupedAnnotations<R>
  );
};

export const transformRecordToTransaction = ({
  record,
  orderId,
  journeyId,
}: {
  record: JourneyTransactionResponse;
  orderId: string;
  journeyId: string;
}): JourneyTransaction => {
  return {
    id: record.id,
    type: ANNOTATION_TYPES.TRANSACTION,
    status: ANNOTATION_RECORD_STATUSES.UNCHANGED,
    orderId,
    journeyId,
    timestamp: record.closest_time_point,
    name: record.name,
    barcode: record.barcode,
    imageUrl: record.image_url,
    skuId: record.sku_base_id,
    variantId: record.variant_id,
    count: record.count,
    action: toCustomerAction(record.update_type),
    gondolaId: record.gondola_id,
    gondolaName: record.gondola_name,
    planogramId: record.planogram_id,
    cameraImageHeight: record.camera_image_height,
    cameraImageWidth: record.camera_image_width,
    pinCoordinates: {
      relativeX: record.item_pin_2d_xy?.[0],
      relativeY: record.item_pin_2d_xy?.[1],
    },
    cameraId: record.camera_local_id,
    calibrationId: record.calibration_id,
    notes: record.notes,
  };
};

export const transformTransactionToRecord = (
  transaction: JourneyTransaction
): JourneyTransactionRequest => {
  return {
    closest_time_point: transaction.timestamp,
    barcode: transaction.barcode,
    image_url: transaction.imageUrl,
    name: transaction.name,
    sku_base_id: transaction.skuId,
    variant_id: transaction.variantId,
    count: transaction.count,
    update_type: toAnnotationAction(transaction.action),
    gondola_id: transaction.gondolaId,
    gondola_name: transaction.gondolaName,
    planogram_id: transaction.planogramId,
    camera_image_height: transaction.cameraImageHeight,
    camera_image_width: transaction.cameraImageWidth,
    item_pin_2d_xy: [
      transaction.pinCoordinates.relativeX,
      transaction.pinCoordinates.relativeY,
    ],
    camera_local_id: transaction.cameraId,
    calibration_id: transaction.calibrationId,
    notes: transaction.notes,
  };
};

export const transformRecordToTrackPersistence = ({
  record,
  orderId,
  journeyId,
}: {
  record: TrackPersistenceResponse;
  orderId: string;
  journeyId: string;
}): TrackPersistence => {
  return {
    id: record.id,
    type: ANNOTATION_TYPES.TRACK_PERSISTENCE,
    status: ANNOTATION_RECORD_STATUSES.UNCHANGED,
    orderId,
    journeyId,
    timestamp: record.closest_time_point,
    cameraImageHeight: record.camera_image_height,
    cameraImageWidth: record.camera_image_width,
    pinCoordinates: {
      relativeX: record.shoulder_center2d_xy[0],
      relativeY: record.shoulder_center2d_xy[1],
    },
    cameraId: record.camera_local_id,
    calibrationId: record.calibration_id,
    notes: record.notes,
  };
};

export const transformTrackPersistenceToRecord = (
  trackPersistence: TrackPersistence
): TrackPersistenceRequest => {
  return {
    closest_time_point: trackPersistence.timestamp,
    camera_image_height: trackPersistence.cameraImageHeight,
    camera_image_width: trackPersistence.cameraImageWidth,
    shoulder_center2d_xy: [
      trackPersistence.pinCoordinates.relativeX,
      trackPersistence.pinCoordinates.relativeY,
    ],
    camera_local_id: trackPersistence.cameraId,
    calibration_id: trackPersistence.calibrationId,
    notes: trackPersistence.notes,
  };
};

export const transformRecordToJourneyProximity = ({
  record,
  orderId,
  journeyId,
}: {
  record: JourneyProximityResponse;
  orderId: string;
  journeyId: string;
}): JourneyProximity => {
  return {
    id: record.id,
    type: ANNOTATION_TYPES.PROXIMITY,
    status: ANNOTATION_RECORD_STATUSES.UNCHANGED,
    orderId,
    journeyId,
    handId: String(record.hand_id) as HandId,
    timestamp: record.closest_time_point,
    cameraImageHeight: record.camera_image_height,
    cameraImageWidth: record.camera_image_width,
    pinCoordinates: {
      relativeX: record.wrist2d_xy[0],
      relativeY: record.wrist2d_xy[1],
    },
    cameraId: record.camera_local_id,
    calibrationId: record.calibration_id,
    gondolaId: record.gondola_id,
    gondolaName: record.gondola_name,
    planogramId: record.planogram_id,
    notes: record.notes,
  };
};

export const transformJourneyProximityToRecord = (
  journeyProximity: JourneyProximity
): JourneyProximityRequest => {
  return {
    hand_id: parseInt(journeyProximity.handId),
    closest_time_point: journeyProximity.timestamp,
    camera_image_height: journeyProximity.cameraImageHeight,
    camera_image_width: journeyProximity.cameraImageWidth,
    wrist2d_xy: [
      journeyProximity.pinCoordinates.relativeX,
      journeyProximity.pinCoordinates.relativeY,
    ],
    camera_local_id: journeyProximity.cameraId,
    calibration_id: journeyProximity.calibrationId,
    gondola_id: journeyProximity.gondolaId,
    gondola_name: journeyProximity.gondolaName,
    planogram_id: journeyProximity.planogramId,
    notes: journeyProximity.notes,
  };
};

export const transformRecordToProximityTimeRange = ({
  record,
  proximity,
  orderId,
  journeyId,
}: {
  record: ProximityTimeRangeResponse;
  proximity: JourneyProximity;
  orderId: string;
  journeyId: string;
}): ProximityTimeRange => {
  return {
    id: record.id,
    type: ANNOTATION_TYPES.PROXIMITY_TIME_RANGES,
    status: ANNOTATION_RECORD_STATUSES.UNCHANGED,
    orderId,
    journeyId,
    proximityId: proximity.id,
    handId: proximity.handId,
    timestamp: proximity.timestamp,
    cameraImageHeight: proximity.cameraImageHeight,
    cameraImageWidth: proximity.cameraImageWidth,
    pinCoordinates: proximity.pinCoordinates,
    gondolaId: proximity.gondolaId,
    gondolaName: proximity.gondolaName,
    planogramId: proximity.planogramId,
    beforeRangeStartTime: record.before_range_start_time,
    beforeRangeEndTime: record.before_range_end_time,
    afterRangeStartTime: record.after_range_start_time,
    afterRangeEndTime: record.after_range_end_time,
    cameraId: record.camera_local_id,
    calibrationId: record.calibration_id,
    notes: record.notes,
  };
};

export const transformProximityTimeRangeToRecord = (
  proximityTimeRange: ProximityTimeRange
): ProximityTimeRangeRequest => {
  return {
    proximity_annotation_id: proximityTimeRange.proximityId,
    before_range_start_time: proximityTimeRange.beforeRangeStartTime,
    before_range_end_time: proximityTimeRange.beforeRangeEndTime,
    after_range_start_time: proximityTimeRange.afterRangeStartTime,
    after_range_end_time: proximityTimeRange.afterRangeEndTime,
    camera_local_id: proximityTimeRange.cameraId,
    calibration_id: proximityTimeRange.calibrationId,
    notes: proximityTimeRange.notes,
  };
};

export const normalizeTimestamp = (timestamp: TimestampValue): number => {
  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }
  if (typeof timestamp === "string") {
    return new Date(timestamp).getTime();
  }
  return timestamp;
};

export const hasIdenticalAnnotationTimestamp = <
  TimestampedEvent extends HasTimestamp,
>(
  existingEvents: TimestampedEvent[],
  newTimestamp: TimestampValue
): boolean => {
  const normalizedNewTimestamp = normalizeTimestamp(newTimestamp);

  return existingEvents.some(
    (event) => normalizeTimestamp(event.timestamp) === normalizedNewTimestamp
  );
};

export const isPinBasedPredictionRecord = (
  item: unknown
): item is PinBasedPredictionRecord => {
  if (!item || typeof item !== "object") return false;
  const record = item as Partial<PinBasedPredictionRecord>;

  // Required fields check
  if (typeof record.name !== "string") return false;
  if (typeof record.barcode !== "string") return false;
  if (typeof record.image_url !== "string") return false;
  if (typeof record.variant_id !== "string") return false;
  if (typeof record.sku_base_id !== "number") return false;
  if (typeof record.update_time !== "string") return false;
  if (!["add", "remove"].includes(record.update_type!)) return false;
  if (typeof record.update_quantity !== "number") return false;

  // Optional fields check
  if (record.track_id !== undefined && typeof record.track_id !== "number")
    return false;
  if (record.gondola_id !== undefined && typeof record.gondola_id !== "number")
    return false;
  if (
    record.gondola_name !== undefined &&
    typeof record.gondola_name !== "string"
  )
    return false;
  if (
    record.camera_image_height !== undefined &&
    typeof record.camera_image_height !== "number"
  )
    return false;
  if (
    record.camera_image_width !== undefined &&
    typeof record.camera_image_width !== "number"
  )
    return false;
  if (
    record.camera_local_id !== undefined &&
    typeof record.camera_local_id !== "number"
  )
    return false;
  if (
    record.calibration_id !== undefined &&
    typeof record.calibration_id !== "number"
  )
    return false;
  if (
    record.update_change_start_time !== undefined &&
    typeof record.update_change_start_time !== "string"
  )
    return false;
  if (
    record.update_change_end_time !== undefined &&
    typeof record.update_change_end_time !== "string"
  )
    return false;
  if (
    record.update_change_camera_local_id !== undefined &&
    typeof record.update_change_camera_local_id !== "number"
  )
    return false;

  if (record.pin2d_xy !== undefined) {
    if (
      !Array.isArray(record.pin2d_xy) ||
      record.pin2d_xy.length !== 2 ||
      typeof record.pin2d_xy[0] !== "number" ||
      typeof record.pin2d_xy[1] !== "number"
    ) {
      return false;
    }
  }

  return true;
};

export const isPinBasedPredictionJson = (
  data: unknown
): data is PinBasedPredictionJson => {
  if (!data || typeof data !== "object") return false;
  const result = (data as Partial<PinBasedPredictionJson>).result;

  if (!result || typeof result !== "object") return false;
  if (typeof result.order_id !== "string") return false;
  if (result.type !== "pin") return false;
  if (!result.updates || typeof result.updates !== "object") return false;

  if (result.request_id !== undefined && typeof result.request_id !== "string")
    return false;
  if (
    result.request_time !== undefined &&
    typeof result.request_time !== "string"
  )
    return false;

  return Object.values(result.updates).every(
    (updates) =>
      Array.isArray(updates) && updates.every(isPinBasedPredictionRecord)
  );
};

export const isZoneBasedPredictionRecord = (
  item: unknown
): item is ZoneBasedPredictionRecord => {
  if (!item || typeof item !== "object") return false;
  const record = item as Partial<ZoneBasedPredictionRecord>;

  // Required fields check
  if (typeof record.name !== "string") return false;
  if (typeof record.barcode !== "string") return false;
  if (typeof record.image_url !== "string") return false;
  if (typeof record.variant_id !== "string") return false;
  if (typeof record.sku_base_id !== "number") return false;
  if (typeof record.update_time !== "string") return false;
  if (!["add", "remove"].includes(record.update_type!)) return false;
  if (typeof record.update_quantity !== "number") return false;

  // Optional fields check
  if (record.gondola_id !== undefined && typeof record.gondola_id !== "number")
    return false;
  if (
    record.gondola_name !== undefined &&
    typeof record.gondola_name !== "string"
  )
    return false;
  if (
    record.camera_image_height !== undefined &&
    typeof record.camera_image_height !== "number"
  )
    return false;
  if (
    record.camera_image_width !== undefined &&
    typeof record.camera_image_width !== "number"
  )
    return false;
  if (
    record.camera_local_id !== undefined &&
    typeof record.camera_local_id !== "number"
  )
    return false;
  if (
    record.calibration_id !== undefined &&
    typeof record.calibration_id !== "number"
  )
    return false;

  if (
    record.update_change_start_time !== undefined &&
    typeof record.update_change_start_time !== "string"
  )
    return false;
  if (
    record.update_change_end_time !== undefined &&
    typeof record.update_change_end_time !== "string"
  )
    return false;
  if (
    record.update_change_camera_local_id !== undefined &&
    typeof record.update_change_camera_local_id !== "number"
  )
    return false;

  if (record.pin2d_xy !== undefined) {
    if (
      !Array.isArray(record.pin2d_xy) ||
      record.pin2d_xy.length !== 2 ||
      typeof record.pin2d_xy[0] !== "number" ||
      typeof record.pin2d_xy[1] !== "number"
    ) {
      return false;
    }
  }

  return true;
};

export const isZoneBasedPredictionJson = (
  data: unknown
): data is ZoneBasedPredictionJson => {
  if (!data || typeof data !== "object") return false;
  const result = (data as Partial<ZoneBasedPredictionJson>).result;

  if (!result || typeof result !== "object") return false;
  if (typeof result.order_id !== "string") return false;
  if (typeof result.request_id !== "string") return false;
  if (typeof result.request_time !== "string") return false;
  if (result.type !== "zone") return false;
  if (!result.updates || typeof result.updates !== "object") return false;

  return Object.values(result.updates).every(
    (updates) =>
      Array.isArray(updates) && updates.every(isZoneBasedPredictionRecord)
  );
};

export const transformRecordToPinPredictions = (
  data: PinBasedPredictionJson
): PinBasedPrediction[] => {
  const { result } = data;

  return Object.entries(result.updates).flatMap(([journeyId, records]) =>
    records.map((record: PinBasedPredictionRecord) => ({
      id: crypto.randomUUID(),
      type: PREDICTION_TYPES.PIN,
      orderId: result.order_id,
      journeyId,
      name: record.name,
      barcode: record.barcode,
      imageUrl: record.image_url,
      variantId: record.variant_id,
      skuId: record.sku_base_id,
      timestamp: record.update_time,
      action: toCustomerAction(record.update_type),
      count: record.update_quantity,
      trackId: record.track_id || undefined,
      gondolaId: record.gondola_id || undefined,
      gondolaName: record.gondola_name || undefined,
      cameraImageHeight: record.camera_image_height || undefined,
      cameraImageWidth: record.camera_image_width || undefined,
      pinCoordinates: record.pin2d_xy
        ? {
            relativeX: record.pin2d_xy[0],
            relativeY: record.pin2d_xy[1],
          }
        : undefined,
      cameraId: record.camera_local_id || undefined,
      calibrationId: record.calibration_id || undefined,
      updateChangeStartTime: record.update_change_start_time || undefined,
      updateChangeEndTime: record.update_change_end_time || undefined,
      updateChangeCameraId: record.update_change_camera_local_id || undefined,
    }))
  );
};

export const transformRecordToZonePredictions = (
  data: ZoneBasedPredictionJson
): ZoneBasedPrediction[] => {
  const { result } = data;

  return Object.entries(result.updates).flatMap(([trackId, records]) =>
    records.map((record: ZoneBasedPredictionRecord) => ({
      id: crypto.randomUUID(),
      type: PREDICTION_TYPES.ZONE,
      orderId: result.order_id,
      trackId,
      name: record.name,
      barcode: record.barcode,
      imageUrl: record.image_url,
      variantId: record.variant_id,
      skuId: record.sku_base_id,
      timestamp: record.update_time,
      action: toCustomerAction(record.update_type),
      count: record.update_quantity,
      gondolaId: record.gondola_id || undefined,
      gondolaName: record.gondola_name || undefined,
      cameraImageHeight: record.camera_image_height || undefined,
      cameraImageWidth: record.camera_image_width || undefined,
      pinCoordinates: record.pin2d_xy
        ? {
            relativeX: record.pin2d_xy[0],
            relativeY: record.pin2d_xy[1],
          }
        : undefined,
      cameraId: record.camera_local_id || undefined,
      calibrationId: record.calibration_id || undefined,
      updateChangeStartTime: record.update_change_start_time || undefined,
      updateChangeEndTime: record.update_change_end_time || undefined,
      updateChangeCameraId: record.update_change_camera_local_id || undefined,
    }))
  );
};

export const getHandSide = (handId: HandId): HandSide => {
  if (handId === "0") return "Left";
  if (handId === "1") return "Right";
  console.error(
    `Invalid hand_id: ${handId}. Expected 0 (left) or 1 (right). Defaulting to Left.`
  );
  return "Left";
};

export const formatTimestamp = (timestamp: Date | string) =>
  dayjs(timestamp).utc().format("DD-MM-YYYY HH:mm:ss.SSS");

export const checkJourneysHaveEnterAndExit = (
  events: JourneyEvent[],
  journeyTransactions: JourneyTransaction[],
  journeys: Journey[]
) => {
  for (const journey of journeys) {
    const journeyEvents = events.filter(
      (event) => event.journeyId === journey.id
    );

    const journeyTransactionEvents = journeyTransactions.filter(
      (journeyTransaction) => journeyTransaction.journeyId === journey.id
    );

    const hasEnterEvent = journeyEvents.some(
      (event) => event.eventType === "enter"
    );
    const hasExitEvent = journeyEvents.some(
      (event) => event.eventType === "exit"
    );

    if (
      journey.status === ANNOTATION_RECORD_STATUSES.DELETED &&
      !journeyEvents.length &&
      !journeyTransactionEvents.length
    ) {
      return true;
    }

    if (!hasEnterEvent || !hasExitEvent) {
      return false;
    }
  }

  return true;
};

export const sortOrdersByTime = (
  orders: Order[] | null,
  sortDirection: SortDirection
) => {
  if (!orders?.length) {
    return [];
  }

  return [...orders].sort((a, b) => {
    const timeA = new Date(a.time).getTime();
    const timeB = new Date(b.time).getTime();
    return sortDirection === SORT_DIRECTIONS.ASC
      ? timeA - timeB
      : timeB - timeA;
  });
};

export const filterAndSortOrders = (
  orders: Order[] | null,
  filters: OrderFilters
) => {
  if (!orders) return null;

  const { source, dateRange, orderId, sortDirection } = filters;

  const filteredOrders = orders.filter((order) => {
    const matchesSource = source === "all" || source === order.order_source;

    let matchesDateRange = true;
    if (dateRange?.from) {
      matchesDateRange = isDateInRange(order.time, dateRange);
    }

    const matchesOrderId =
      !orderId || order.id.toLowerCase().includes(orderId.toLowerCase());

    return matchesSource && matchesDateRange && matchesOrderId;
  });

  return sortOrdersByTime(filteredOrders, sortDirection);
};

const isDateInRange = (orderTime: string, dateRange: DateRange) => {
  const orderDate = dayjs(orderTime).utcOffset(0, true);
  const fromDate = dayjs(dateRange.from).utcOffset(0, true).startOf("day");
  const toDate = dateRange.to
    ? dayjs(dateRange.to).utcOffset(0, true).endOf("day")
    : fromDate.endOf("day");

  return orderDate.isBetween(fromDate, toDate, null, "[]");
};

export const getMinMaxDates = (orders: Order[] | null) => {
  if (!orders || orders.length === 0) {
    return { minDate: null, maxDate: null };
  }

  let minDate = dayjs.utc(orders[0].time);
  let maxDate = dayjs.utc(orders[0].time);

  for (const order of orders) {
    const orderDate = dayjs.utc(order.time);
    if (orderDate.isBefore(minDate)) {
      minDate = orderDate;
    }
    if (orderDate.isAfter(maxDate)) {
      maxDate = orderDate;
    }
  }

  return {
    minDate: minDate.utc().startOf("day").toDate(),
    maxDate: maxDate.utc().startOf("day").toDate(),
  };
};

export const formatDate = (date: Date | undefined): string => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getDateRangeText = (range: DateRange | null): string => {
  const fromDate = range?.from;
  const toDate = range?.to;

  if (!fromDate) return "Select Range";
  if (!toDate) return formatDate(fromDate);

  if (fromDate.toDateString() === toDate.toDateString()) {
    return formatDate(fromDate);
  }

  return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
};

export const formatDateWithoutTimezone = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const isValidDateString = (dateString: string) => {
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateFormatRegex.test(dateString)) {
    return false;
  }

  const [year, month, day] = dateString.split("-").map(Number);

  if (month < 1 || month > 12) {
    return false;
  }

  const date = parse(dateString, "yyyy-MM-dd", new Date());
  if (!isValid(date)) {
    return false;
  }

  return (
    date.getFullYear() === year &&
    date.getMonth() + 1 === month &&
    date.getDate() === day
  );
};

export const getListOfSources = (orders: Order[] | null): SourceOption[] => {
  if (!orders) return [];

  const sourcesSet = new Set(orders.map((order) => order.order_source));

  return Array.from(sourcesSet)
    .filter(Boolean)
    .map((source) => ({
      key: source,
      value: source
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    }));
};

export const isObserverCamera = (selectedCamera: string): boolean => {
  return ["119", "136"].includes(selectedCamera);
};

export const formatCoordinateToThreeDecimals = (
  coordinate?: number | number
): string => {
  return coordinate ? coordinate.toFixed(3) : "";
};

export const getFirstNonHandTrackingCamera = (
  gondola: Gondola | null
): string | null => {
  if (!gondola) return null;

  const firstNonHandTracking = gondola.shelfTrackingCameras?.[0]?.id;

  return firstNonHandTracking || null;
};

export const findEventById = (
  collections: EventCollections,
  eventId: string
) => {
  if (!eventId) return null;

  const searchableCollections = Object.keys(collections) as Array<
    keyof EventCollections
  >;

  for (const collectionName of searchableCollections) {
    const found = collections?.[collectionName]?.find(
      (item: EventSource) => item.id === eventId
    );
    if (found) return found;
  }

  return null;
};

export const highlightTimelineItem = (
  dataset: DataSet<any, any>,
  selectedOrder: Order | null,
  eventId?: string | null
) => {
  dataset?.forEach((item) => {
    const color =
      item.id === selectedOrder?.id
        ? TIMELINE_ITEM_COLORS.PURPLE
        : item.color === TIMELINE_ITEM_COLORS.RED
          ? TIMELINE_ITEM_COLORS.MEDIUM_GRAY
          : item.color;
    dataset.update({
      id: item.id,
      color,
    });
  });

  if (eventId) {
    dataset.update({
      id: eventId,
      color: TIMELINE_ITEM_COLORS.RED,
    });
  }
};

type CartItem = {
  skuId: number;
  name: string;
  barcode: string;
  quantity: number;
  imageUrl: string;
};

export const createVirtualCartFromTransactions = (
  virtualCartItems: JourneyTransaction[]
) => {
  const itemMap = new Map<number, CartItem>();

  virtualCartItems.forEach((transaction) => {
    const quantity =
      transaction.action === CUSTOMER_ACTIONS.TAKE
        ? transaction.count
        : -transaction.count;
    const currentItem = itemMap.get(transaction.skuId);

    if (currentItem) {
      currentItem.quantity += quantity;
      if (currentItem.quantity === 0) {
        itemMap.delete(transaction.skuId);
      }
    } else {
      itemMap.set(transaction.skuId, {
        skuId: transaction.skuId,
        name: transaction.name,
        barcode: transaction.barcode,
        quantity: quantity,
        imageUrl: transaction.imageUrl,
      });
    }
  });

  return Array.from(itemMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};
